/**
 * Returns the useful payload from the API response.
 *
 * The PHP backend responds with a wrapper shaped like:
 * {
 *   status: "success",
 *   message: "Success",
 *   data: {...},
 *   timestamp: "..."
 * }
 *
 * Older versions of this helper rejected the wrapper because `timestamp`
 * was not an allowed metadata key. As a result, chart arrays were converted
 * to [] and overview fields were read from the wrapper rather than `data`.
 */
export const extractPayload = (response, fallback = null) => {
  let value = response;

  for (let index = 0; index < 5; index += 1) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      break;
    }

    const hasData = Object.prototype.hasOwnProperty.call(value, "data");
    const looksLikeApiWrapper =
      hasData &&
      (Object.prototype.hasOwnProperty.call(value, "status") ||
        Object.prototype.hasOwnProperty.call(value, "success") ||
        Object.prototype.hasOwnProperty.call(value, "message") ||
        Object.prototype.hasOwnProperty.call(value, "timestamp"));

    if (!looksLikeApiWrapper) {
      break;
    }

    value = value.data;
  }

  return value ?? fallback;
};

export const extractArray = (response) => {
  const value = extractPayload(response, []);
  return Array.isArray(value) ? value : [];
};

export const extractObject = (response) => {
  const value = extractPayload(response, {});
  return value && typeof value === "object" && !Array.isArray(value)
    ? value
    : {};
};

/** Convert selected MySQL numeric strings into real numbers for Recharts. */
export const normalizeNumericFields = (records, fields) =>
  records.map((record) => {
    const normalized = { ...record };

    fields.forEach((field) => {
      if (normalized[field] === null || normalized[field] === undefined) {
        normalized[field] = 0;
        return;
      }

      const number = Number(normalized[field]);
      if (Number.isFinite(number)) {
        normalized[field] = number;
      }
    });

    return normalized;
  });
